<template>
  <div class="s-playing-now__options__filters">
    <template
      v-for="(filter, filterName) in filtersDataFiltered"
      :key="filterName">
      <div class="s-playing-now__options__filters__element">
        <select
          :value="modelValue[filterName]"
          class="form-select"
          :class="{ 'form-select--mobile': !desktop }"
          :disabled="isDisabled(filterName)"
          @change="(e) => filterOptions(e, filterName)">
          <option value="All" selected>
            {{ equivalentPlaceholder(filterName) }}
          </option>
          <option
            v-for="filterOption in filter"
            :key="filterOption.value"
            :value="filterOption.value"
            :disabled="isOnline(filterName, filterOption)">
            {{ filterOption.display }}
          </option>
        </select>
      </div>
    </template>
  </div>
</template>

<script setup>
import { getCurrentInstance, onMounted, ref, watch } from 'vue'
import useViewport from '../../features/viewport.js'
import * as lib from '../../lib/lib.js'
import { uniq } from 'underscore'
const { dictionary } = getCurrentInstance().appContext.config.globalProperties
const { desktop } = useViewport()
const screens = lib.getData('bookingData').screens

// props
const props = defineProps({
  filtersData: {
    default() {
      return {}
    },
    type: Object,
    required: true,
  },
  placeholderCinemas: {
    default: 'All',
    type: String,
  },
  placeholderScreenType: {
    default: 'All',
    type: String,
  },
  placeholderRoomType: {
    default: 'All',
    type: String,
  },
  modelValue: {
    default() {
      return {
        cinemas: 'All',
        screenTypes: 'All',
        roomTypes: 'All',
      }
    },
    type: Object,
  },
})

// emits
const emit = defineEmits(['filter-changed', 'update:modelValue'])

// reactive data
const filters = ref({
  cinemas: [],
  screenTypes: [],
  roomTypes: [],
})
const selectedFilters = ref({
  cinemas: 'All',
  screenTypes: 'All',
  roomTypes: 'All',
})

const filtersDataFiltered = ref({})

const isOnline = (filterName, filterOption) => {
  if (filterName === 'cinemas' && !filterOption.isOnline) {
    return true
  }
  return false
}

const isDisabled = (filterName) => {
  if (Object.keys(props.filtersData[filterName]).length === 0) {
    return true
  }
  return false
}

// hooks
watch(
  props.filtersData,
  () => {
    assignFiltersData()
  },
  { deep: true },
)

onMounted(() => {
  assignFiltersData()

  const selectedCinema = props.filtersData.cinemas.find((c) => c.isSelected)

  if (selectedCinema) {
    selectedFilters.value.cinemas = selectedCinema.value
    setFiltersByKey('cinemas')
  }

  selectedFilters.value.cinemas =
    props.filtersData.cinemas.find((c) => c.isSelected)?.value || 'All'

  setFiltersByKey('cinemas')

  emit('filter-changed', selectedFilters.value)
  emit('update:modelValue', selectedFilters.value)
})

// methods
const assignFiltersData = () => {
  filters.value = {
    cinemas: props.filtersData.cinemas,
    roomTypes: props.filtersData.roomTypes,
    screenTypes: props.filtersData.screenTypes,
  }

  filtersDataFiltered.value = { ...filters.value }
}

const filterOptions = (e, filterName) => {
  selectedFilters.value[filterName] = e.target.value

  setFiltersByKey(filterName)
  emit('filter-changed', selectedFilters.value)
  emit('update:modelValue', selectedFilters.value)
}

const setFiltersByKey = (key) => {
  const availableCinemas = uniq(
    filterScreens(['screenTypes', 'roomTypes']).map((i) => i.cinemaId),
  )
  const availableScreenTypes = uniq(
    filterScreens(['cinemas', 'roomTypes']).map((i) => i.screenType),
  )
  const availableRoomTypes = uniq(
    filterScreens(['cinemas', 'screenTypes'])
      .map((i) => i.roomTypes)
      .flat(),
  )

  const { cinemas, screenTypes, roomTypes } = filters.value

  filtersDataFiltered.value = {
    ...filtersDataFiltered.value,

    ...(key !== 'cinemas' && {
      cinemas: cinemas.filter((item) => availableCinemas.includes(item.value)),
    }),
    ...(key !== 'screenTypes' && {
      screenTypes: screenTypes.filter((item) =>
        availableScreenTypes.includes(item.value),
      ),
    }),
    ...(key !== 'roomTypes' && {
      roomTypes: roomTypes.filter((item) =>
        availableRoomTypes.includes(item.value),
      ),
    }),
  }
}

const filterScreens = (filters) => {
  const { cinemas, screenTypes, roomTypes } = selectedFilters.value

  const cinemasIsFilter = filters.includes('cinemas')
  const screenTypesIsFilter = filters.includes('screenTypes')
  const roomTypesIsFilter = filters.includes('roomTypes')

  const cinemasHasValue = cinemas && cinemas !== 'All'
  const screenTypesHasValue = screenTypes && screenTypes !== 'All'
  const roomTypesHasValue = roomTypes && roomTypes !== 'All'

  return screens.filter((x) => {
    // prettier-ignore
    return (
      (cinemasHasValue && cinemasIsFilter ? x.cinemaId === cinemas : true) &&
      (screenTypesHasValue && screenTypesIsFilter ? x.screenType === screenTypes : true) &&
      (roomTypesHasValue && roomTypesIsFilter ? x.roomTypes.includes(roomTypes) : true)
    )
  })
}

const equivalentPlaceholder = (filterName) => {
  if (filterName === 'cinemas') {
    return dictionary.placeholderCinemas
  } else if (filterName === 'screenTypes') {
    return dictionary.placeholderScreenType
  } else if (filterName === 'roomTypes') {
    return dictionary.placeholderRoomType
  }
  return 'All'
}
</script>
